import React from 'react';
import { ArrowLeft, Shield, Eye, Lock, Database } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <Link to="/" className="btn-secondary" style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '32px' 
            }}>
              <ArrowLeft size={16} />
              Back to Home
            </Link>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              Privacy Policy
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              We are committed to protecting your privacy and ensuring the security of your personal health information.
            </p>
            <div className="body-small" style={{ color: 'var(--text-light)' }}>
              Last Updated: January 2025
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Overview */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '60px'
          }}>
            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <Shield size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Data Protection
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Your personal and health information is encrypted and securely stored
              </p>
            </div>

            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <Eye size={40} style={{ color: 'var(--brand-green)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Transparency
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Clear information about what data we collect and how it's used
              </p>
            </div>

            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <Lock size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Your Control
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Manage your privacy preferences and data sharing settings
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Policy */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            
            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Information We Collect
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                marginBottom: '24px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px', color: 'var(--brand-green)' }}>
                  Personal Information
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• Name, address, phone number, and email address</li>
                  <li className="body-regular">• Date of birth and gender for prescription verification</li>
                  <li className="body-regular">• Payment information (processed securely through third-party processors)</li>
                  <li className="body-regular">• Emergency contact information</li>
                  <li className="body-regular">• Government-issued ID for prescription validation</li>
                </ul>
              </div>
              
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                marginBottom: '24px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px', color: 'var(--brand-blue)' }}>
                  Health Information
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• Prescription information and medical history</li>
                  <li className="body-regular">• Drug allergies and adverse reactions</li>
                  <li className="body-regular">• Insurance information and claims</li>
                  <li className="body-regular">• Healthcare provider information</li>
                  <li className="body-regular">• Purchase history of medicines and health products</li>
                </ul>
              </div>

              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                  Technical Information
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• IP address and browser information</li>
                  <li className="body-regular">• Website usage data and analytics</li>
                  <li className="body-regular">• Device information and preferences</li>
                  <li className="body-regular">• Cookies and similar technologies</li>
                  <li className="body-regular">• Location data (with permission)</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                How We Use Your Information
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '24px'
              }}>
                {[
                  { title: 'Prescription Services', description: 'Process and dispense prescription medications safely and accurately' },
                  { title: 'Healthcare Support', description: 'Provide medication counseling and health-related information' },
                  { title: 'Order Processing', description: 'Fulfill orders, process payments, and arrange delivery services' },
                  { title: 'Communication', description: 'Send important health alerts, refill reminders, and service updates' }
                ].map((item, index) => (
                  <div key={index} style={{
                    padding: '24px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '0px',
                    border: '1px solid var(--border-light)'
                  }}>
                    <Database size={24} style={{ 
                      color: index % 2 === 0 ? 'var(--brand-blue)' : 'var(--brand-green)', 
                      marginBottom: '12px' 
                    }} />
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                      {item.title}
                    </h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Information Sharing & Disclosure
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                border: '1px solid var(--brand-green)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                  We Share Information Only When:
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• Required by law or legal process</li>
                  <li className="body-regular">• With healthcare providers for treatment coordination</li>
                  <li className="body-regular">• With insurance companies for claims processing</li>
                  <li className="body-regular">• With regulatory authorities for safety reporting</li>
                  <li className="body-regular">• With service providers under strict confidentiality agreements</li>
                  <li className="body-regular">• With your explicit consent for specific purposes</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Data Security & Protection
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  We employ industry-standard security measures to protect your personal and health information:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <li className="body-regular">• SSL encryption for all data transmissions</li>
                  <li className="body-regular">• Secure servers with restricted access</li>
                  <li className="body-regular">• Regular security audits and updates</li>
                  <li className="body-regular">• Employee training on privacy and confidentiality</li>
                  <li className="body-regular">• Compliance with healthcare data protection standards</li>
                  <li className="body-regular">• Backup and disaster recovery procedures</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Your Rights & Choices
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                  You Have the Right To:
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• Access and review your personal information</li>
                  <li className="body-regular">• Request corrections to inaccurate information</li>
                  <li className="body-regular">• Opt-out of marketing communications</li>
                  <li className="body-regular">• Request deletion of certain personal data</li>
                  <li className="body-regular">• Restrict processing of your information</li>
                  <li className="body-regular">• Request a copy of your data in portable format</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Cookies & Analytics
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  We use cookies and similar technologies to improve your experience and analyze website usage:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <li className="body-regular">• Essential cookies for website functionality</li>
                  <li className="body-regular">• Analytics cookies to understand user behavior</li>
                  <li className="body-regular">• Preference cookies to remember your settings</li>
                  <li className="body-regular">• Google Analytics for website performance monitoring</li>
                </ul>
                <p className="body-small" style={{ color: 'var(--text-light)', marginTop: '16px' }}>
                  You can control cookie preferences through your browser settings.
                </p>
              </div>
            </div>

            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Contact Us
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  For questions about this privacy policy or to exercise your rights, please contact us:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="body-regular">
                    <strong>Privacy Officer:</strong> +91-6358585323
                  </div>
                  <div className="body-regular">
                    <strong>Email:</strong> carepharmacyindia@gmail.com
                  </div>
                  <div className="body-regular">
                    <strong>Address:</strong> Care Pharmacy Pvt Ltd, Wall Street-2, Opp. Orient Club, Gujarat College Road, Ahmedabad-380006
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;